
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BarChart3, Target, AlertCircle, Activity } from "lucide-react";
import { MarketSummary } from '@/services/MarketSummaryService';
import { getTrendConfluenceBadge, getMarketConditionBadge, getActionBadge, getAlertSeverityBadge } from './utils/badgeHelpers';

interface MarketAnalysisDetailDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedSummary: MarketSummary | null;
}

export const MarketAnalysisDetailDialog = ({ 
  isOpen, 
  onOpenChange, 
  selectedSummary 
}: MarketAnalysisDetailDialogProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Enhanced Market Analysis - {selectedSummary?.symbol}
            {selectedSummary?.signals_analysis?.current_price && (
              <Badge variant="outline" className="font-mono">
                ${selectedSummary.signals_analysis.current_price.toLocaleString()}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {selectedSummary && (
          <ScrollArea className="max-h-[70vh]">
            <div className="space-y-6 pr-4">
              {/* Trading Recommendations Section */}
              {selectedSummary.trading_recommendations && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Target className="h-5 w-5 text-green-500" />
                    Trading Recommendations
                  </h3>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center justify-between">
                        Recommended Action
                        {getActionBadge(selectedSummary.trading_recommendations.action)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <div className="text-sm text-muted-foreground">Position Size</div>
                          <div className="font-medium">{selectedSummary.trading_recommendations.position_size}</div>
                        </div>
                        {selectedSummary.trading_recommendations.stop_loss && (
                          <div>
                            <div className="text-sm text-muted-foreground">Stop Loss</div>
                            <div className="font-medium text-red-600">
                              ${parseFloat(selectedSummary.trading_recommendations.stop_loss).toLocaleString()}
                            </div>
                          </div>
                        )}
                        {selectedSummary.trading_recommendations.take_profit && (
                          <div>
                            <div className="text-sm text-muted-foreground">Take Profit</div>
                            <div className="font-medium text-green-600">
                              ${parseFloat(selectedSummary.trading_recommendations.take_profit).toLocaleString()}
                            </div>
                          </div>
                        )}
                        {selectedSummary.trading_recommendations.risk_reward_ratio && (
                          <div>
                            <div className="text-sm text-muted-foreground">Risk/Reward</div>
                            <div className="font-medium">1:{selectedSummary.trading_recommendations.risk_reward_ratio}</div>
                          </div>
                        )}
                      </div>
                      <div className="mt-4 p-3 bg-muted rounded-lg">
                        <div className="text-sm font-medium mb-1">Reasoning</div>
                        <div className="text-sm text-muted-foreground">
                          {selectedSummary.trading_recommendations.reasoning}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Separator />
                </div>
              )}

              {/* Market Alerts Section */}
              {selectedSummary.market_alerts && selectedSummary.market_alerts.alerts && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                    Market Alerts ({selectedSummary.market_alerts.alert_count})
                  </h3>
                  
                  <div className="space-y-3">
                    {selectedSummary.market_alerts.alerts.map((alert: any, index: number) => (
                      <Card key={index}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                {getAlertSeverityBadge(alert.severity)}
                                <Badge variant="outline" className="text-xs">{alert.type}</Badge>
                              </div>
                              <div className="text-sm">{alert.message}</div>
                              {alert.level && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Level: ${parseFloat(alert.level).toLocaleString()} 
                                  {alert.distance_pct && ` (${alert.distance_pct}% away)`}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  <Separator />
                </div>
              )}

              {/* Technical Analysis Section */}
              {selectedSummary.signals_analysis && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-500" />
                    Technical Analysis
                  </h3>
                  
                  {/* Trend Confluence & Market Condition */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Trend Confluence</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {getTrendConfluenceBadge(selectedSummary.trend_confluence)}
                          <div className="text-sm text-muted-foreground">
                            Confidence: {selectedSummary.signals_analysis.trend_analysis.confluence_score}%
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base">Market Condition</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {getMarketConditionBadge(selectedSummary.market_condition)}
                          <div className="text-sm text-muted-foreground">
                            Technical Score: {selectedSummary.technical_confidence_score}%
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Support & Resistance Levels */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedSummary.support_levels && selectedSummary.support_levels.length > 0 && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base text-green-600">Support Levels</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {selectedSummary.support_levels.slice(0, 5).map((level, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-green-900/20 border border-green-600/30 rounded hover:bg-green-800/30 transition-colors">
                                <span className="font-medium text-white">${level.level.toLocaleString()}</span>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">{level.timeframe}</Badge>
                                  <span className="text-xs text-green-400">R{level.reliability}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    
                    {selectedSummary.resistance_levels && selectedSummary.resistance_levels.length > 0 && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base text-red-600">Resistance Levels</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            {selectedSummary.resistance_levels.slice(0, 5).map((level, index) => (
                              <div key={index} className="flex justify-between items-center p-2 bg-red-900/20 border border-red-600/30 rounded hover:bg-red-800/30 transition-colors">
                                <span className="font-medium text-white">${level.level.toLocaleString()}</span>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">{level.timeframe}</Badge>
                                  <span className="text-xs text-red-400">R{level.reliability}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                  
                  <Separator />
                </div>
              )}

              {/* Sentiment Analysis Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Sentiment Analysis</h3>
                
                {/* Stats Overview */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {selectedSummary.num_long}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Long ({selectedSummary.percent_long}%)
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {selectedSummary.num_short}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Short ({selectedSummary.percent_short}%)
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">
                        {selectedSummary.num_neutral}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Neutral ({selectedSummary.percent_neutral}%)
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {selectedSummary.combined_confidence_score || selectedSummary.confidence_score}%
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Combined Score
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Keywords */}
                {selectedSummary.top_keywords && selectedSummary.top_keywords.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Top Keywords</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedSummary.top_keywords.map((keyword, index) => (
                          <Badge key={index} variant="outline">
                            {keyword}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Combined Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Market Ideas Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-48 w-full">
                      <div className="text-sm leading-relaxed whitespace-pre-wrap">
                        {selectedSummary.total_summary}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};
